import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CalendarModule } from 'primeng/calendar';
import { MessageService, ConfirmationService } from 'primeng/api';

import { CasTransaction as Transaction, CasTransactionService } from '../../services/cas-transaction.service';
import { CasContact as Contact, CasContactService } from '../../services/cas-contact.service';
import { ProjectHistoryService } from '../../services/project-history.service';
import { RingoverService } from '../../../cac/services/ringover.service';
import { AuthService } from '../../../../shared/service/auth.service';
import { STATUT_LEAD_OPTIONS } from '../cas-contacts/cas-contacts.component';
import { UserService, User as UserProfile } from '../../../../shared/service/user.service';
import {
  typeRepriseOptions, deplacementOptions, travailEnHauteurOptions,
  origines as origineOptions,
} from '../../../cac/models/enum';

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#14b8a6','#f59e0b','#10b981','#3b82f6','#ef4444','#f97316','#06b6d4'];

export const PHASES_TRANSACTION = [
  'Nouveau LEAD / RDV',
  'Echange',
  'Devis envoyé',
  'Relance & objections en cours',
  'Questionnaire médical envoyé',
  'Fermé gagné',
  'Fermé perdu',
  'EVENEMENT / AVIS / PARRAINAGE',
];

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    TableModule, ButtonModule, InputTextModule,
    DropdownModule, DialogModule, TagModule,
    ToastModule, ProgressSpinnerModule, TooltipModule, ConfirmDialogModule,
    CalendarModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './cas-transactions.component.html',
  styleUrls: ['./cas-transactions.component.scss'],
})
export class CasTransactionsComponent implements OnInit {
  @ViewChild('csvImportInput') csvImportInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileDeleteExcelInput') mobileDeleteExcelInput?: ElementRef<HTMLInputElement>;

  private readonly filtersKey = 'transactionsFiltersState';
  transactions: Transaction[] = [];
  totalCount   = 0;
  loading      = false;
  openingForm  = false;
  importingCsv = false;
  deletingByExcel = false;

  // ---------- filtres ----------
  contactId     : number | null  = null;
  selectedPhase : string | null  = null;
  search        = '';
  showFilters   = true;
  /** Verrou sur un projet précis (ouverture depuis l'historique) : n'affiche
   *  que la (les) transaction(s) rattachée(s) à ce project_id exact. */
  lockedProjectId : string | null = null;

  // filtres avancés
  selectedOrigine : string | null = null;
  selectedUserId  : number | null = null;
  dateRange       : Date[] | null = null;
  origineFilterOptions = origineOptions;
  usersFilter     : { label: string; value: number }[] = [];
  isAdmin         = false;
  currentUserId   : number | null = null;

  phaseFilterOptions = [
    { label: 'Toutes les phases', value: null as string | null },
    ...PHASES_TRANSACTION.map(p => ({ label: p, value: p })),
  ];

  // ---------- actions de masse ----------
  selectedAction       : string | null = null;
  selectedAssignUserId : number | null = null;
  exportingExcel       = false;
  sendingBulkInfo      = false;
  sendingBulkSavings   = false;
  sendingBulkSms       = false;
  assigningBulk        = false;
  duplicatingId        : number | null = null;
  private agentProfile : UserProfile | null = null;

  // ---------- SMS relecture (dialog avant envoi) ----------
  smsDialogVisible     = false;
  smsDialogContact     : Contact | null = null;
  smsDialogPhone       = '';
  smsDialogContent     = '';
  smsDialogSending     = false;

  // ---------- édition inline ----------
  phaseEditOptions          = PHASES_TRANSACTION;
  statutLeadOptions         = STATUT_LEAD_OPTIONS;
  statutContratOptions      = ['En cours', 'Signé', 'Refusé', 'Résilié', 'En attente'];
  typeRepriseOptions        = typeRepriseOptions;
  deplacementOptions        = deplacementOptions;
  travailEnHauteurOptions   = travailEnHauteurOptions;
  resultatAppelOptions      = ['OK', 'NRP'];
  ouiNonOptions             = [{ label: 'Non', value: false }, { label: 'Oui', value: true }];



  // ---------- sélection groupée ----------
  selected : Transaction[] = [];
  bulkPhase: string | null = null;

  // ---------- détails (transaction + fiche contact) ----------
  detailVisible       = false;
  selectedTransaction : Transaction | null = null;
  selectedContact     : Contact | null = null;
  loadingContact      = false;

  // ---------- création ----------
  createVisible        = false;
  saving               = false;
  newTransaction       : Partial<Transaction> = {};
  contactOptions       : { label: string; value: number }[] = [];
  loadingContactOpts   = false;

  constructor(
    private CasTransactionService: CasTransactionService,
    private CasContactService: CasContactService,
    private projectHistoryService: ProjectHistoryService,
    private ringoverService: RingoverService,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    const raw = this.route.snapshot.queryParamMap.get('contactId');
    this.contactId = raw ? Number(raw) : null;
    const projectIdParam = (this.route.snapshot.queryParamMap.get('projectId') || '').trim();
    this.lockedProjectId = projectIdParam || null;
    this.isAdmin = this.authService.isAdmin();
    this.currentUserId = this.getCurrentUserIdFromToken();
    if (!this.contactId && !this.lockedProjectId) this.restoreFilters();
    if (this.isAdmin) this.loadUsers();
    this.CasTransactionService.count().subscribe({ next: (r) => this.totalCount = r.count, error: () => {} });
    this.load();
  }

  private restoreFilters(): void {
    try {
      const rawState = sessionStorage.getItem(this.filtersKey);
      if (!rawState) return;
      const s = JSON.parse(rawState);
      this.search          = s.search || '';
      this.selectedPhase   = s.selectedPhase ?? null;
      this.selectedOrigine = s.selectedOrigine ?? null;
      this.selectedUserId  = s.selectedUserId ?? null;
      if (Array.isArray(s.dateRange) && s.dateRange.length === 2 && s.dateRange[0] && s.dateRange[1]) {
        this.dateRange = [new Date(s.dateRange[0]), new Date(s.dateRange[1])];
      }
    } catch { sessionStorage.removeItem(this.filtersKey); }
  }

  private persistFilters(): void {
    sessionStorage.setItem(this.filtersKey, JSON.stringify({
      search: this.search,
      selectedPhase: this.selectedPhase,
      selectedOrigine: this.selectedOrigine,
      selectedUserId: this.selectedUserId,
      dateRange: this.dateRange && this.dateRange.length === 2
        ? [this.dateRange[0]?.toISOString(), this.dateRange[1]?.toISOString()] : null,
    }));
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const uid = Number(decoded?.user_id);
      return Number.isFinite(uid) && uid > 0 ? uid : null;
    } catch { return null; }
  }

  private loadUsers(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        this.usersFilter = (users || [])
          .filter(u => u.is_active !== false)
          .map(u => ({ label: u.username || u.email, value: u.id }));
      },
      error: () => {},
    });
  }

  // ===== création =====

  openCreate(): void {
    this.newTransaction = {};
    this.createVisible  = true;
    if (!this.contactOptions.length) {
      this.loadingContactOpts = true;
      this.CasContactService.list(500, 0).subscribe({
        next: (contacts) => {
          this.contactOptions = (contacts || []).map(c => ({
            label : [[c.prenom, c.nom].filter(Boolean).join(' '), c.mobile].filter(Boolean).join(' · ') + ` (#${c.id})`,
            value : c.id,
          }));
          this.loadingContactOpts = false;
        },
        error: () => { this.loadingContactOpts = false; },
      });
    }
  }

  fillExampleData(): void {
    const contactId = this.contactOptions.length > 0 ? this.contactOptions[0].value : undefined;
    this.newTransaction = {
      contact_id: contactId,
      phase_transaction: 'Nouveau',
      date_contact: '2026-07-20',
      date_effective: '2026-09-01',
      date_envoi_devis: '2026-07-20',
      resultat_appel: 'OK',
      commentaire: 'Prospect très intéressé, souhaite une mutuelle familiale.',
      revenu: 45000,
      deplacement_professionnel: true,
      travaille_en_hauteur: false,
      port_charge: false,
      nb_enfant_assure: 2,
      nbre_devis_envoyes: 1,
      devis_envoye: true,
      nom_assureurs: 'SwissLife, April',
      numero_devis: 'DEV-20260720-001',
      cout_mensuel_assurance: 120.50,
      precedent_assureur: 'Axa',
    };
  }

  saveTransaction(): void {
    if (!this.newTransaction.contact_id) {
      this.messageService.add({ severity: 'warn', summary: 'Requis', detail: 'Sélectionnez un contact.' });
      return;
    }

    this.saving = true;
    this.CasTransactionService.create(this.newTransaction).subscribe({
      next: (t) => {
        this.transactions.unshift(t);
        this.saving = false;
        this.createVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Transaction créée', detail: t.reference || t.project_id || '' });
      },
      error: (err) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de créer la transaction.' });
        console.error(err);
      },
    });
  }

  // ===== données =====

  load(): void {
    this.loading = true;
    if (!this.contactId) this.persistFilters();
    this.CasTransactionService.list(500, 0, {
      contact_id       : this.contactId    ?? undefined,
      phase_transaction: this.selectedPhase ?? undefined,
    }).subscribe({
      next: (data) => { this.transactions = data || []; this.loading = false; },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les transactions' });
        console.error(err);
      },
    });
  }

  clearContactFilter(): void {
    this.contactId = null;
    this.router.navigate(['/cac/transactions']);
    this.load();
  }

  // ===== filtres côté client =====

  get filteredTransactions(): Transaction[] {
    const q = this.search.trim().toLowerCase();
    const [start, end] = this.dateRange && this.dateRange.length === 2 ? this.dateRange : [null, null];
    return this.transactions.filter(t => {
      // verrou projet (ouverture depuis l'historique) : transaction liée uniquement
      if (this.lockedProjectId && t.project_id !== this.lockedProjectId) return false;
      // recherche texte
      if (q) {
        const match = (
          (t.reference         || '').toLowerCase().includes(q) ||
          (t.project_id        || '').toLowerCase().includes(q) ||
          
          (t.assureur          || '').toLowerCase().includes(q) ||
          
          
          (t.contact ? [t.contact.prenom, t.contact.nom, t.contact.mobile].filter(Boolean).join(' ').toLowerCase().includes(q) : false) ||
          false
        );
        if (!match) return false;
      }
      // origine
      if (this.selectedOrigine && t.origine !== this.selectedOrigine) return false;
      // conseiller (admin)
      if (this.selectedUserId != null && Number(t.user_id) !== this.selectedUserId) return false;
      // plage de dates (sur date_creation_transaction)
      if (start && end && t.date_creation_transaction) {
        const d = new Date(t.date_creation_transaction);
        const s = new Date(start); s.setHours(0, 0, 0, 0);
        const e = new Date(end);   e.setHours(23, 59, 59, 999);
        if (d < s || d > e) return false;
      }
      return true;
    });
  }

  get hasActiveFilters(): boolean {
    return !!(this.search.trim() || this.selectedPhase || this.contactId ||
      this.selectedOrigine || this.selectedUserId != null || this.lockedProjectId ||
      (this.dateRange && this.dateRange.length === 2));
  }

  clearFilters(): void {
    this.search          = '';
    this.selectedPhase   = null;
    this.contactId       = null;
    this.selectedOrigine = null;
    this.selectedUserId  = null;
    this.dateRange       = null;
    this.lockedProjectId = null;
    this.router.navigate(['/cac/transactions']);
    this.load();
  }

  // ===== actions ligne =====

  openDetails(t: Transaction): void {
    this.selectedTransaction = t;
    this.selectedContact     = null;
    this.detailVisible       = true;
    if (t.contact_id != null) {
      this.loadingContact = true;
      this.CasContactService.get(t.contact_id).subscribe({
        next: (c) => { this.selectedContact = c; this.loadingContact = false; },
        error: (err) => {
          this.loadingContact = false;
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger la fiche contact.' });
          console.error(err);
        },
      });
    }
  }

  fullName(c: Contact): string {
    return [c.prenom, c.nom].filter(Boolean).join(' ') || '—';
  }

  initials(c: Contact): string {
    const f = (c.prenom || '').charAt(0);
    const l = (c.nom || '').charAt(0);
    return (f + l).toUpperCase() || '?';
  }

  callContact(c: Contact): void {
    const number = (c.mobile || c.fixe || '').replace(/\s/g, '');
    if (!number) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de numéro de téléphone." });
      return;
    }
    // Ouvre Ringover avec le numéro (protocole tel:).
    window.location.href = `tel:${number}`;
    // En parallèle : suivi serveur → Résultat appel (OK/NRP) sur la transaction.
    const tx = this.selectedTransaction;
    this.ringoverService.trackCall({
      to_number: number,
      contact_id: c.id,
      transaction_id: tx?.id,
    }).subscribe({
      next: (res) => {
        if (res?.calls_read) {
          this.scheduleResultatAppelRefresh(tx?.id ?? null);
        } else {
          this.messageService.add({
            severity: 'warn', summary: 'Suivi appel désactivé',
            detail: res?.message || "La clé Ringover n'a pas la permission 'Calls Read'.",
            life: 8000,
          });
        }
      },
      error: () => {},
    });
  }

  /** Rafraîchit le champ Résultat appel de la transaction après la fenêtre de
   *  suivi serveur (~30s + 10×25s) : sonde à 120s puis 300s. */
  private scheduleResultatAppelRefresh(transactionId: number | null): void {
    if (transactionId == null) return;
    const refresh = () => {
      this.CasTransactionService.get(transactionId).subscribe({
        next: (fresh) => {
          const row = this.transactions.find(x => x.id === transactionId);
          if (row) row.resultat_appel = fresh.resultat_appel;
          const sel = this.selectedTransaction;
          if (sel && sel.id === transactionId) sel.resultat_appel = fresh.resultat_appel;
        },
        error: () => {},
      });
    };
    setTimeout(refresh, 120000);
    setTimeout(refresh, 300000);
  }

  /** Construit le message SMS "Rappel Devis" pré-rédigé pour un contact. */
  private buildSmsRappel(c: Contact, sig: string): string {
    const nom = c.nom || '';
    const civ = c.civilite || '';
    const greeting = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ.trim().toLowerCase().replace(/\./g, '')) ? 'Chère' : 'Cher';
    const smsCivilite = this.formatCiviliteForSms(civ);
    const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
    return `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\nLe cas échéant, pouvons-nous échanger 5 minutes ?\n\nBien à vous\n\n${sig}`;
  }

  /** Ouvre le dialog SMS pré-rédigé pour relecture avant envoi (via API Ringover). */
  smsContact(c: Contact): void {
    const phone = (c.mobile || '').replace(/\s/g, '');
    if (!phone) {
      this.messageService.add({ severity: 'warn', summary: 'Pas de numéro', detail: "Ce contact n'a pas de mobile renseigné." });
      return;
    }
    const openDialog = (agent: UserProfile) => {
      this.smsDialogContact = c;
      this.smsDialogPhone   = phone;
      this.smsDialogContent = this.buildSmsRappel(c, this.buildSignature(agent));
      this.smsDialogVisible = true;
    };
    if (this.agentProfile) openDialog(this.agentProfile);
    else this.userService.getMyProfile().subscribe({
      next: (p) => { this.agentProfile = p; openDialog(p); },
      error: () => openDialog({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null } as any),
    });
  }

  /** Envoi effectif du SMS depuis le dialog (après relecture). */
  sendSmsFromDialog(): void {
    if (!this.smsDialogContact || !this.smsDialogPhone || !this.smsDialogContent.trim()) return;
    this.smsDialogSending = true;
    this.ringoverService.sendSms({ to_number: this.smsDialogPhone, content: this.smsDialogContent }).subscribe({
      next: () => {
        this.smsDialogSending = false;
        this.smsDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'SMS envoyé', detail: `À ${this.smsDialogPhone}`, life: 4000 });
      },
      error: (err) => {
        this.smsDialogSending = false;
        this.messageService.add({ severity: 'error', summary: 'Échec SMS', detail: err?.error?.detail || 'Impossible d\'envoyer le SMS via Ringover.' });
        console.error(err);
      },
    });
  }

  openInForm(t: Transaction): void {
    if (!t.contact_id) {
      this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Aucun contact lié à cette transaction." });
      return;
    }
    this.openingForm = true;
    this.CasContactService.get(t.contact_id).subscribe({
      next: (contact) => {
        this.openingForm = false;
        const queryParams: any = {};
        if (contact.nom) queryParams.nom = contact.nom;
        if (contact.prenom) queryParams.prenom = contact.prenom;
        if (contact.civilite) queryParams.civilite = contact.civilite;
        if (contact.mobile) queryParams.telephone = contact.mobile;
        if (contact.email) queryParams.email = contact.email;
        if (contact.ville) queryParams.ville = contact.ville;
        
        this.router.navigate(['/cas/compare'], { queryParams });
      },
      error: (err) => {
        this.openingForm = false;
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: "Impossible de charger le contact pour ouvrir le formulaire." });
        console.error(err);
      },
    });
  }

  deleteOne(t: Transaction): void {
    this.confirmationService.confirm({
      message: `Supprimer la transaction ${t.reference || t.project_id} ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.CasTransactionService.delete(t.id).subscribe({
          next: () => {
            this.transactions = this.transactions.filter(x => x.id !== t.id);
            this.selected     = this.selected.filter(x => x.id !== t.id);
            this.messageService.add({ severity: 'success', summary: 'Supprimée', detail: t.reference || '' });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression impossible' });
            console.error(err);
          },
        });
      },
    });
  }

  deleteSelected(): void {
    if (!this.selected.length) return;
    this.confirmationService.confirm({
      message: `Supprimer ${this.selected.length} transaction(s) ?`,
      header: 'Confirmation de suppression groupée',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        forkJoin(this.selected.map(t => this.CasTransactionService.delete(t.id))).subscribe({
          next: () => {
            const ids = new Set(this.selected.map(t => t.id));
            this.transactions = this.transactions.filter(x => !ids.has(x.id));
            this.selected     = [];
            this.messageService.add({ severity: 'success', summary: 'Supprimées', detail: `${ids.size} transaction(s)` });
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Suppression groupée impossible' });
            console.error(err);
            this.load();
          },
        });
      },
    });
  }

  applyBulkPhase(): void {
    if (!this.bulkPhase || !this.selected.length) return;
    const phase = this.bulkPhase;
    forkJoin(this.selected.map(t => this.CasTransactionService.update(t.id, { phase_transaction: phase }))).subscribe({
      next: () => {
        this.selected.forEach(t => (t.phase_transaction = phase));
        this.messageService.add({ severity: 'success', summary: 'Phase mise à jour', detail: `${this.selected.length} transaction(s)` });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Changement de phase groupé impossible' });
        console.error(err);
        this.load();
      },
    });
  }

  updateContactField(c: Contact, field: keyof Contact, value: any): void {
    this.CasContactService.update(c.id, { [field]: value } as Partial<Contact>).subscribe({
      next: () => {
        (c as any)[field] = value;
      },
    });
  }

  updateField(t: Transaction, field: keyof Transaction, value: any): void {
    this.CasTransactionService.update(t.id, { [field]: value } as Partial<Transaction>).subscribe({
      next: (fresh) => {
        (t as any)[field] = value;
        // Le serveur peut faire évoluer la phase automatiquement (ex: devis
        // envoyé → « Devis envoyé »). On reflète la phase renvoyée dans la ligne.
        if (fresh?.phase_transaction) {
          t.phase_transaction = fresh.phase_transaction;
        }
        this.messageService.add({ severity: 'success', summary: 'Mis à jour', detail: `${field} modifié`, life: 1500 });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Mise à jour impossible' });
        console.error(err);
        this.load();
      },
    });
  }

  // ===== Duplication =====

  duplicateOne(t: Transaction): void {
    if (!t.project_id) {
      this.messageService.add({ severity: 'warn', summary: 'Indisponible', detail: 'Aucun dossier CAC lié.' });
      return;
    }
    this.confirmationService.confirm({
      message: `Dupliquer la transaction ${t.reference || t.project_id} ?`,
      header: 'Confirmer la duplication',
      icon: 'pi pi-copy',
      acceptLabel: 'Dupliquer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.duplicatingId = t.id;
        this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Duplication non supportée en Santé.' });
      },
    });
  }

  // ===== Menu d'actions unifié (global + sélection) =====

  get actionOptions(): { label: string; value: string; icon: string; needsSelection: boolean }[] {
    const opts: { label: string; value: string; icon: string; needsSelection: boolean; admin?: boolean }[] = [
      { label: 'Exporter en Excel',            value: 'export',        icon: 'pi pi-file-excel',  needsSelection: false },
      { label: 'Importer (CSV OGGO)',          value: 'import_csv',    icon: 'pi pi-file-import', needsSelection: false, admin: true },
      { label: 'Supprimer (Excel)',            value: 'delete_excel',  icon: 'pi pi-trash',       needsSelection: false, admin: true },
      { label: 'Changer la phase',             value: 'phase',         icon: 'pi pi-flag',        needsSelection: true },
      { label: 'Email demande d\'information', value: 'email_info',    icon: 'pi pi-envelope',    needsSelection: true },
      { label: 'Email économies',              value: 'email_savings', icon: 'pi pi-percentage',  needsSelection: true },
      { label: 'SMS rappel devis',             value: 'sms_rappel',    icon: 'pi pi-comment',     needsSelection: true },
      { label: 'Attribuer à un conseiller',    value: 'assign',        icon: 'pi pi-users',       needsSelection: true, admin: true },
      { label: 'Supprimer la sélection',       value: 'delete',        icon: 'pi pi-trash',       needsSelection: true },
    ];
    return opts.filter(o => !o.admin || this.isAdmin);
  }

  actionNeedsSelection(value: string | null): boolean {
    return ['phase', 'email_info', 'email_savings', 'sms_rappel', 'assign', 'delete'].includes(value || '');
  }

  canExecuteAction(): boolean {
    if (!this.selectedAction || this.isBulkBusy()) return false;
    if (this.actionNeedsSelection(this.selectedAction) && !this.selected.length) return false;
    if (this.selectedAction === 'phase'  && !this.bulkPhase) return false;
    if (this.selectedAction === 'assign' && !this.selectedAssignUserId) return false;
    return true;
  }

  executeAction(): void {
    switch (this.selectedAction) {
      case 'export':        this.exportExcel(); break;
      case 'import_csv':    this.triggerCsvImport(); break;
      case 'delete_excel':  this.triggerMobileDeleteExcel(); break;
      case 'phase':         this.applyBulkPhase(); break;
      case 'email_info':    this.sendBulkInfoEmail(); break;
      case 'email_savings': this.sendBulkSavingsEmail(); break;
      case 'sms_rappel':    this.sendBulkSmsRappel(); break;
      case 'assign':        this.assignBulk(); break;
      case 'delete':        this.deleteSelected(); break;
    }
  }

  // ===== Export Excel =====

  exportExcel(): void {
    if (this.exportingExcel) return;
    const ids = this.selected.map(t => t.project_id).filter(Boolean);
    const useSelection = ids.length > 0;
    const exportIds = useSelection ? ids : this.filteredTransactions.map(t => t.project_id).filter(Boolean);
    if (!exportIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Export', detail: 'Aucune transaction à exporter.' });
      return;
    }
    this.exportingExcel = false;
    this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Export non supporté en Santé.' });
  }

  // ===== Emails groupés (réutilise ProjectHistory via project_id) =====

  private bulkProjectIds(): string[] {
    return this.selected.map(t => t.project_id).filter(Boolean);
  }

  sendBulkInfoEmail(): void {
    const ids = this.bulkProjectIds();
    if (!ids.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer l'email de demande d'information pour ${ids.length} transaction(s) ?`,
      header: 'Confirmer l\'envoi groupé', icon: 'pi pi-envelope',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkInfo = true;
        this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Bulk email non supporté en Santé.' });
      },
    });
  }

  sendBulkSavingsEmail(): void {
    const ids = this.bulkProjectIds();
    if (!ids.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer l'email d'économies pour ${ids.length} transaction(s) ?`,
      header: 'Confirmer l\'envoi groupé', icon: 'pi pi-percentage',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkSavings = true;
        this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Bulk email non supporté en Santé.' });
      },
    });
  }

  // ===== SMS rappel devis groupé (Ringover) =====

  private transactionPhoneE164(t: Transaction): string | null {
    const raw = (t.contact?.mobile || '').trim();
    if (!raw) return null;
    const d = raw.replace(/\D/g, '');
    if (!d || d.length < 8) return null;
    if (d.startsWith('00')) return `+${d.slice(2)}`;
    if (d.startsWith('33')) return `+${d}`;
    if (d.startsWith('0'))  return `+33${d.slice(1)}`;
    return `+${d}`;
  }

  private buildSignature(agent: UserProfile): string {
    const lines: string[] = [];
    if (agent.username) lines.push(agent.username.trim());
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS'); lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  private formatCiviliteForSms(civilite: string | undefined): string {
    const raw = (civilite || '').trim();
    const normalized = raw.toLowerCase().replace(/\./g, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(normalized)) return 'Mme';
    if (['m', 'mr', 'monsieur'].includes(normalized)) return 'M.';
    return raw;
  }

  sendBulkSmsRappel(): void {
    const withPhone = this.selected.filter(t => this.transactionPhoneE164(t));
    const without = this.selected.length - withPhone.length;
    if (!withPhone.length) { this.messageService.add({ severity: 'warn', summary: 'Aucun numéro', detail: 'Aucune transaction sélectionnée n\'a de mobile.' }); return; }
    this.confirmationService.confirm({
      message: `Envoyer un SMS "Rappel Devis" à ${withPhone.length} contact(s) ?` + (without > 0 ? `\n(${without} ignoré(s) — sans mobile)` : ''),
      header: 'Confirmer l\'envoi SMS', icon: 'pi pi-comment',
      acceptLabel: 'Envoyer', rejectLabel: 'Annuler',
      accept: () => {
        const launch = (agent: UserProfile) => {
          const sig = this.buildSignature(agent);
          this.sendingBulkSms = true;
          let sent = 0, failed = 0; const total = withPhone.length;
          withPhone.forEach(t => {
            const phone = this.transactionPhoneE164(t)!;
            const nom = t.contact?.nom || '';
            const civ = t.contact?.civilite || '';
            const greeting = ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ.trim().toLowerCase().replace(/\./g, '')) ? 'Chère' : 'Cher';
            const smsCivilite = this.formatCiviliteForSms(civ);
            const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
            const content = `${salutation},\n\nVous nous avez sollicités pour des devis en assurance de crédit. Nous vous avions envoyé plusieurs devis.\nVotre besoin est-il toujours d'actualité ?\nLe cas échéant, pouvons-nous échanger 5 minutes ?\n\nBien à vous\n\n${sig}`;
            this.ringoverService.sendSms({ to_number: phone, content }).subscribe({
              next: () => { sent++; if (sent + failed === total) this.onBulkSmsDone(sent, failed); },
              error: () => { failed++; if (sent + failed === total) this.onBulkSmsDone(sent, failed); },
            });
          });
        };
        if (this.agentProfile) launch(this.agentProfile);
        else this.userService.getMyProfile().subscribe({
          next: (p) => { this.agentProfile = p; launch(p); },
          error: () => launch({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null } as any),
        });
      },
    });
  }

  private onBulkSmsDone(sent: number, failed: number): void {
    this.sendingBulkSms = false;
    this.messageService.add({
      severity: sent > 0 ? 'success' : 'error',
      summary: sent > 0 ? 'SMS envoyés' : 'Échec SMS',
      detail: sent > 0 ? `${sent} SMS envoyé(s)${failed > 0 ? ` (${failed} échec(s))` : ''}.` : `Aucun SMS envoyé (${failed} erreur(s)).`,
      life: 8000,
    });
  }

  // ===== Attribution groupée (admin) =====

  assignBulk(): void {
    if (!this.isAdmin) return;
    if (!this.selectedAssignUserId) { this.messageService.add({ severity: 'warn', summary: 'Conseiller', detail: 'Choisissez un conseiller.' }); return; }
    if (!this.selected.length) { this.messageService.add({ severity: 'warn', summary: 'Sélection vide', detail: 'Aucune transaction sélectionnée.' }); return; }
    const target = this.selectedAssignUserId;
    const label = this.usersFilter.find(u => u.value === target)?.label || 'le conseiller';
    this.confirmationService.confirm({
      message: `Attribuer ${this.selected.length} transaction(s) à ${label} ?`,
      header: 'Confirmer l\'attribution', icon: 'pi pi-users',
      acceptLabel: 'Confirmer', rejectLabel: 'Annuler',
      accept: () => {
        this.assigningBulk = true;
        forkJoin(this.selected.map(t => this.CasTransactionService.update(t.id, { user_id: target } as any))).subscribe({
          next: () => {
            this.assigningBulk = false;
            this.messageService.add({ severity: 'success', summary: 'Attribuées', detail: `${this.selected.length} transaction(s) attribuée(s) à ${label}.` });
            this.selectedAction = null; this.selectedAssignUserId = null; this.selected = [];
            this.load();
          },
          error: (err) => { this.assigningBulk = false; this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Attribution impossible.' }); console.error(err); },
        });
      },
    });
  }

  isBulkBusy(): boolean {
    return this.exportingExcel || this.sendingBulkInfo || this.sendingBulkSavings
      || this.sendingBulkSms || this.assigningBulk || this.importingCsv || this.deletingByExcel;
  }

  // ===== Import CSV OGGO (admin) — réutilise ProjectHistory =====

  triggerCsvImport(): void {
    if (!this.isAdmin || this.importingCsv) return;
    this.csvImportInput?.nativeElement.click();
  }

  onCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
      this.messageService.add({ severity: 'warn', summary: 'Format invalide', detail: 'Sélectionnez un fichier CSV.' });
      input.value = ''; return;
    }
    this.importingCsv = true;
    this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Import CSV non supporté en Santé.' });
  }

  // ===== Suppression par Excel mobile (admin) — réutilise ProjectHistory =====

  triggerMobileDeleteExcel(): void {
    if (!this.isAdmin || this.deletingByExcel) return;
    this.mobileDeleteExcelInput?.nativeElement.click();
  }

  onMobileDeleteExcelSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      this.messageService.add({ severity: 'warn', summary: 'Format invalide', detail: 'Sélectionnez un fichier Excel .xlsx.' });
      input.value = ''; return;
    }
    this.confirmationService.confirm({
      message: `Importer "${file.name}" pour supprimer/vider les numéros listés ?`,
      header: 'Confirmer la suppression par Excel', icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Exécuter', rejectLabel: 'Annuler', acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deletingByExcel = true;
        this.messageService.add({ severity: 'warn', summary: 'Non supporté', detail: 'Suppression par Excel non supportée en Santé.' });
      },
      reject: () => { input.value = ''; },
    });
  }

  // ===== helpers =====

  parseDevisHistory(t: Transaction): any[] {
    if (!t.nom_assureurs) return [];
    try {
      const parsed = JSON.parse(t.nom_assureurs);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return [];
  }

  contactName(t: Transaction): string {
    const c = t.contact;
    if (!c) return '—';
    return [c.prenom, c.nom].filter(Boolean).join(' ') || c.reference || `#${c.id}`;
  }

  contactInitials(t: Transaction): string {
    const c = t.contact;
    if (!c) return '?';
    const f = (c.prenom || '').charAt(0);
    const l = (c.nom || '').charAt(0);
    return (f + l).toUpperCase() || '?';
  }

  contactAvatarColor(t: Transaction): string {
    const id = t.contact?.id ?? t.contact_id ?? 0;
    return AVATAR_COLORS[id % AVATAR_COLORS.length];
  }

  hasDevis(t: Transaction): boolean {
    return t.devis_envoye === true || Number(t.nbre_devis_envoyes) > 0;
  }

  phaseSeverity(phase?: string): any {
    switch (phase) {
      case 'Fermé gagné':                   return 'success';
      case 'Fermé perdu':                   return 'danger';
      case 'Devis envoyé':
      case 'Questionnaire médical envoyé':  return 'info';
      case 'Relance & objections en cours': return 'warning';
      default:                              return 'secondary';
    }
  }
}
